import { ErrorInfo } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import packageInfo from '../../package.json';

const resizeObserverErrorMessages = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded'
];

const isResizeObserverError = (message?: string) =>
  resizeObserverErrorMessages.some((text) => !!message?.includes(text));

const isAbortError = (message?: string) =>
  !!message?.includes('AbortError') || !!message?.includes('signal is aborted');

const isMarkerIOScreenCaptureError = (message?: string) =>
  !!message?.includes('Marker.io') && !!message?.includes('STREAM_NOT_AVAILABLE');

const initDatadog = () => {
  if (
    import.meta.env.VITE_APP_TITLE === 'production' ||
    import.meta.env.VITE_APP_TITLE === 'development'
  ) {
    // Initialize Logs
    datadogLogs.init({
      beforeSend: (event) => {
        if (event?.message && typeof event.message === 'string') {
          if (
            isAbortError(event.message) ||
            isResizeObserverError(event.message) ||
            isMarkerIOScreenCaptureError(event.message)
          ) {
            return false;
          }
        }
        return true;
      },
      clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
      env: `env:${import.meta.env.VITE_APP_TITLE}`,
      forwardConsoleLogs: ['error'],
      forwardErrorsToLogs: true,
      service: import.meta.env.VITE_DATA_DOG_SERVICE,
      site: 'datadoghq.com',
      version: packageInfo.version
    });
    // Initialize RUM
    datadogRum.init({
      allowedTracingUrls: [import.meta.env.VITE_API_URL],
      applicationId: import.meta.env.VITE_DATA_DOG_APP_ID,
      beforeSend: (event) => {
        if (event.type === 'error') {
          const errorMessage = event?.error?.message || '';
          const eventMessage = typeof event?.message === 'string' ? event.message : '';

          // Skip AbortError because it’s an expected behavior —
          // the request is aborted intentionally to prevent unnecessary backend calls
          // and doesn’t indicate a real application error.
          if (isAbortError(errorMessage) || isAbortError(eventMessage)) {
            return false;
          }

          /// Avoid STREAM_NOT_AVAILABLE when native screen capture (getDisplayMedia) is unavailable (e.g. in iframes)
          if (
            isMarkerIOScreenCaptureError(errorMessage) ||
            isMarkerIOScreenCaptureError(eventMessage)
          ) {
            return false;
          }

          if (
            isResizeObserverError(errorMessage) ||
            isResizeObserverError(eventMessage) ||
            isResizeObserverError(event?.error?.stack ?? '')
          ) {
            // It is a benign warning generated by the browser's safety mechanisms,
            // triggered by a script (most likely a user's browser extension) that is creating a layout loop.
            // The browser is successfully preventing a freeze and just logging this message as a warning.
            // It is not crashing the app for the user.
            return false;
          }
        }

        return true;
      },
      clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
      defaultPrivacyLevel: 'mask-user-input',
      enablePrivacyForActionName: false,
      env: `env:${import.meta.env.VITE_APP_TITLE}`,
      service: import.meta.env.VITE_DATA_DOG_SERVICE,
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      site: 'datadoghq.com',
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      version: packageInfo.version
    });

    // Set up CSP violation tracking
    if (typeof window !== 'undefined' && 'addEventListener' in window) {
      window.addEventListener('securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
        // Track as an error in RUM for better visibility
        datadogRum.addError('CSP Violation', {
          blockedURI: e.blockedURI,
          columnNumber: e.columnNumber,
          disposition: e.disposition,
          effectiveDirective: e.effectiveDirective,
          lineNumber: e.lineNumber,
          source: 'csp',
          sourceFile: e.sourceFile,
          violatedDirective: e.violatedDirective
        });

        // Log CSP violations to DataDog Logs for detailed analysis
        datadogLogs.logger.error('CSP Violation', {
          blockedURI: e.blockedURI,
          columnNumber: e.columnNumber,
          disposition: e.disposition,
          documentURI: e.documentURI,
          effectiveDirective: e.effectiveDirective,
          lineNumber: e.lineNumber,
          originalPolicy: e.originalPolicy,
          referrer: e.referrer || document.referrer,
          sourceFile: e.sourceFile,
          statusCode: e.statusCode,
          type: 'csp_violation',
          violatedDirective: e.violatedDirective
        });
      });
    }
  }
};

const logError = (
  error: Error,
  info: ErrorInfo,
  logLevel?: 'error' | 'warn' | 'info' | 'critical'
) => {
  datadogLogs.logger[logLevel || 'error'](error.message, { ...info, isHandled: true });
};

export { initDatadog, logError };
